import React, { useEffect, useState, useCallback } from 'react';
import { FaSearch } from 'react-icons/fa';
import { Recipe } from '../../types/recipe';
import { recipeApi } from '../../services/api';
import { RecipeCard } from './RecipeCard';
import { Button } from '../ui/Button';
import styles from './RecipeList.module.css';

interface RecipeListProps {
  onRecipeClick: (recipe: Recipe) => void;
  onRecipeEdit: (recipe: Recipe) => void;
  onRecipeDelete: (recipe: Recipe) => Promise<void> | void;
}

const PAGE_SIZE = 12;

const formatCountLabel = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

export const RecipeList: React.FC<RecipeListProps> = ({
  onRecipeClick,
  onRecipeEdit,
  onRecipeDelete
}) => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadRecipes = useCallback(async (pageNumber: number, search?: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await recipeApi.list({
        skip: pageNumber * PAGE_SIZE,
        take: PAGE_SIZE,
        search
      });
      
      if (pageNumber === 0) {
        setRecipes(response.recipes);
      } else {
        setRecipes(prev => [...prev, ...response.recipes]);
      }

      setTotalCount(response.pagination.total);
      setHasMore(pageNumber * PAGE_SIZE + response.recipes.length < response.pagination.total);
    } catch (err) {
      setError('Failed to load recipes. Please try again.');
      console.error('Error loading recipes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset and load recipes when search changes
  useEffect(() => {
    setPage(0);
    loadRecipes(0, debouncedSearch);
  }, [debouncedSearch, loadRecipes]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadRecipes(nextPage, debouncedSearch);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleRetry = () => {
    setPage(0);
    loadRecipes(0, debouncedSearch);
  };

  const handleRecipeDelete = async (recipe: Recipe) => {
    try {
      await onRecipeDelete(recipe);
      setPage(0);
      await loadRecipes(0, debouncedSearch);
    } catch (err) {
      setError('Failed to delete recipe. Please try again.');
      console.error('Error deleting recipe:', err);
    }
  };

  const activeSearch = debouncedSearch.trim();
  const resultSummary =
    loading && recipes.length === 0
      ? 'Loading saved recipes...'
      : activeSearch.length > 0
        ? totalCount === 0
          ? `No recipes match "${activeSearch}".`
          : `Showing ${formatCountLabel(totalCount, 'recipe')} for "${activeSearch}".`
        : totalCount === 0
          ? 'No recipes saved yet.'
          : `${formatCountLabel(totalCount, 'recipe')} saved in your library.`;

  const showInitialLoading = loading && recipes.length === 0;
  const showLoadingMore = loading && recipes.length > 0;

  return (
    <div className={styles.page}>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitleBlock}>
            <p className={styles.sectionEyebrow}>Recipe library</p>
            <h2 className={styles.sectionTitle}>Browse saved recipes</h2>
            <p className={styles.sectionMeta} role="status">
              {resultSummary}
            </p>
          </div>
          <div className={styles.summaryBadge}>
            <span className={styles.summaryLabel}>{activeSearch ? 'Results' : 'Saved recipes'}</span>
            <strong className={styles.summaryValue}>
              {showInitialLoading ? '...' : totalCount}
            </strong>
          </div>
        </div>

        <div className={styles.searchBar}>
          <label className={styles.searchLabel} htmlFor="recipe-search">
            Search recipes
          </label>
          <div className={styles.searchInputWrap}>
            <span className={styles.searchIcon} aria-hidden="true">
              {FaSearch({ 'aria-hidden': 'true' })}
            </span>
            <input
              id="recipe-search"
              type="text"
              className={styles.searchInput}
              placeholder="Search recipes by title, description, or ingredients..."
              value={searchQuery}
              onChange={handleSearchChange}
              aria-label="Search recipes"
            />
          </div>
        </div>

        {error ? (
          <div className={styles.error}>
            <p>{error}</p>
            <Button variant="primary" size="md" onClick={handleRetry}>
              Retry
            </Button>
          </div>
        ) : (
          <>
            {showInitialLoading && (
              <div className={styles.loading}>
                Loading...
              </div>
            )}

            {!loading && recipes.length > 0 && (
              <div className={styles.list}>
                {recipes.map(recipe => (
                  <div key={recipe.id} className={styles.listItem}>
                    <RecipeCard
                      recipe={recipe}
                      onEdit={() => onRecipeEdit(recipe)}
                      onDelete={() => void handleRecipeDelete(recipe)}
                      onClick={() => onRecipeClick(recipe)}
                    />
                  </div>
                ))}
              </div>
            )}

            {showLoadingMore && (
              <div className={styles.loadingMore}>
                Loading...
              </div>
            )}

            {!loading && hasMore && (
              <div className={styles.footerAction}>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleLoadMore}
                >
                  Load More
                </Button>
              </div>
            )}

            {!loading && recipes.length === 0 && (
              <div className={styles.empty}>
                <p>No recipes found. Create your first recipe!</p>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};
